import { LoadingState, PageContainer } from '@osteopro/ui';
import { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'wouter';
import { AccessGate } from './components/routing/AccessGate.js';
import { PlatformRoute } from './components/routing/PlatformRoute.js';
import { ProtectedRoute } from './components/routing/ProtectedRoute.js';
import { AppLayout } from './components/shell/AppLayout.js';
import { helpArticlePath, helpCategoryPath } from './help/paths.js';
import { useAuth } from './lib/auth-provider.js';

/**
 * Table de routes. Chaque page est chargée à la demande : la V1 importait ses ~75 pages en tête
 * d'`App.tsx`, ce qui mettait l'application entière dans le premier paquet.
 */

const LoginPage = lazy(async () => import('./pages/auth/LoginPage.js'));
const RegisterPage = lazy(async () => import('./pages/auth/RegisterPage.js'));
const ForgotPasswordPage = lazy(async () => import('./pages/auth/ForgotPasswordPage.js'));
const ResetPasswordPage = lazy(async () => import('./pages/auth/ResetPasswordPage.js'));
const AcceptInvitationPage = lazy(async () => import('./pages/auth/AcceptInvitationPage.js'));
const VerifyEmailPage = lazy(async () => import('./pages/auth/VerifyEmailPage.js'));
const BookingPage = lazy(async () => import('./pages/public/BookingPage.js'));
const PublicRegistrationPage = lazy(async () => import('./pages/public/PublicRegistrationPage.js'));
const FollowUpPage = lazy(async () => import('./pages/public/FollowUpPage.js'));
const CapturePage = lazy(async () => import('./pages/public/CapturePage.js'));
const ReviewPage = lazy(async () => import('./pages/public/ReviewPage.js'));
const LandingPage = lazy(async () => import('./pages/public/LandingPage.js'));
const LegalPage = lazy(async () => import('./pages/public/LegalPage.js'));
const PdfPreviewPage = lazy(async () => import('./pages/public/PdfPreviewPage.js'));
const AgendaPage = lazy(async () => import('./pages/agenda/AgendaPage.js'));
const DashboardPage = lazy(async () => import('./pages/dashboard/DashboardPage.js'));
const InvoicesPage = lazy(async () => import('./pages/invoices/InvoicesPage.js'));
const RetrocessionsPage = lazy(async () => import('./pages/retrocessions/RetrocessionsPage.js'));
const ConsultationReasonsPage = lazy(
  async () => import('./pages/consultation-reasons/ConsultationReasonsPage.js'),
);
const ConsultationFormPage = lazy(
  async () => import('./pages/consultations/ConsultationFormPage.js'),
);
const NewConsultationPage = lazy(
  async () => import('./pages/consultations/NewConsultationPage.js'),
);
const QuestionnairesPage = lazy(async () => import('./pages/questionnaires/QuestionnairesPage.js'));
const PatientsListPage = lazy(async () => import('./pages/patients/PatientsListPage.js'));
const PatientImportPage = lazy(async () => import('./pages/patients/PatientImportPage.js'));
const PatientDetailPage = lazy(async () => import('./pages/patients/PatientDetailPage.js'));
const SettingsPage = lazy(async () => import('./pages/settings/SettingsPage.js'));
const PlatformActivityPage = lazy(async () => import('./pages/platform/PlatformActivityPage.js'));
const PlatformOverviewPage = lazy(async () => import('./pages/platform/PlatformOverviewPage.js'));
const PlatformOrganizationsPage = lazy(
  async () => import('./pages/platform/PlatformOrganizationsPage.js'),
);
const PlatformOrganizationPage = lazy(
  async () => import('./pages/platform/PlatformOrganizationPage.js'),
);
const PlatformUsersPage = lazy(async () => import('./pages/platform/PlatformUsersPage.js'));
const PlatformServicesPage = lazy(async () => import('./pages/platform/PlatformServicesPage.js'));
const PlatformTicketsPage = lazy(async () => import('./pages/platform/PlatformTicketsPage.js'));
const PlatformTicketPage = lazy(async () => import('./pages/platform/PlatformTicketPage.js'));
const SupportTicketsPage = lazy(async () => import('./pages/support/SupportTicketsPage.js'));
const SupportTicketPage = lazy(async () => import('./pages/support/SupportTicketPage.js'));
const HelpCenterPage = lazy(async () => import('./pages/help/HelpCenterPage.js'));
const HelpArticlePage = lazy(async () => import('./pages/help/HelpArticlePage.js'));
const MandatoryOnboardingPage = lazy(
  async () => import('./pages/onboarding/MandatoryOnboardingPage.js'),
);
const WelcomeWizardPage = lazy(async () => import('./pages/onboarding/WelcomeWizardPage.js'));
const SubscriptionSuccessPage = lazy(
  async () => import('./pages/subscription/SubscriptionSuccessPage.js'),
);
const SubscriptionCancelledPage = lazy(
  async () => import('./pages/subscription/SubscriptionCancelledPage.js'),
);
const NotFoundPage = lazy(async () => import('./pages/NotFoundPage.js'));

function PageFallback() {
  return (
    <PageContainer data-testid="state-loading">
      <LoadingState type="card" count={2} />
    </PageContainer>
  );
}

/**
 * Zone authentifiée. Les murs — cabinet **suspendu**, cabinet **bloqué** faute d'abonnement —
 * sont posés par `AccessGate`, sous la garde de session : les droits qu'il lit exigent une session.
 */
function ProtectedArea() {
  return (
    <ProtectedRoute>
      <AccessGate>
        <AuthenticatedShell />
      </AccessGate>
    </ProtectedRoute>
  );
}

function AuthenticatedShell() {
  return (
    <AppLayout>
      <Suspense fallback={<PageFallback />}>
        <Switch>
          {/* `/calendar` était le chemin canonique de la V1 : conservé en redirection. */}
          <Route path="/calendar">
            <Redirect to="/agenda" replace />
          </Route>
          <Route path="/appointments">
            <Redirect to="/agenda" replace />
          </Route>
          <Route path="/dashboard" component={DashboardPage} />
          {/* `/billing` et `/pending-reminders` étaient les chemins de la V1 : conservés en
                redirection vers le hub unique. */}
          <Route path="/billing">
            <Redirect to="/invoices" replace />
          </Route>
          <Route path="/pending-reminders">
            <Redirect to="/invoices?tab=reminders" replace />
          </Route>
          {/* L'écran des impayés est devenu le filtre « Impayées » de l'onglet Factures. */}
          <Route path="/invoices/unpaid">
            <Redirect to="/invoices?filter=unpaid" replace />
          </Route>
          <Route path="/invoices" component={InvoicesPage} />
          <Route path="/retrocessions" component={RetrocessionsPage} />
          <Route path="/agenda" component={AgendaPage} />
          <Route path="/consultation-reasons" component={ConsultationReasonsPage} />
          <Route path="/questionnaires" component={QuestionnairesPage} />
          <Route path="/consultations/new" component={NewConsultationPage} />
          {/* Une consultation sans section ouvre son premier écran : la V1 avait trois
                redirections distinctes pour la même chose. */}
          <Route path="/consultations/:encounterId">
            {(params: { encounterId: string }) => (
              <Redirect to={`/consultations/${params.encounterId}/motif`} replace />
            )}
          </Route>
          <Route path="/consultations/:encounterId/:section" component={ConsultationFormPage} />
          <Route path="/patients" component={PatientsListPage} />
          {/* Avant `/patients/:patientId`, qui prendrait sinon « import » pour un identifiant. */}
          <Route path="/patients/import" component={PatientImportPage} />
          <Route path="/patients/:patientId" component={PatientDetailPage} />
          <Route path="/settings">
            <Redirect to="/settings/profile" replace />
          </Route>
          <Route path="/settings/:section" component={SettingsPage} />
          {/* Aide du cabinet. Ouverte à tout membre : c'est la porte que l'écran de cabinet
              suspendu désigne, et la fermer laisserait le praticien sans recours. */}
          <Route path="/aide" component={SupportTicketsPage} />
          {/* Les trois guides d'avant le centre d'Aide sont devenus des articles. Leurs adresses
              restent, en redirection, pour les liens déjà partagés. */}
          <Route path="/aide/guides/demarrage">
            <Redirect to={helpArticlePath('bienvenue')} replace />
          </Route>
          <Route path="/aide/guides/faq">
            <Redirect to={helpCategoryPath('faq')} replace />
          </Route>
          <Route path="/aide/guides/wero">
            <Redirect to={helpArticlePath('wero-buckaroo')} replace />
          </Route>
          {/* Centre d'Aide en Markdown, avant `/aide/:ticketId` qui prendrait « articles ». */}
          <Route path="/aide/articles" component={HelpCenterPage} />
          <Route path="/aide/articles/:slug" component={HelpArticlePage} />
          <Route path="/aide/:ticketId" component={SupportTicketPage} />
          {/* Mise en route obligatoire : identité, autorisation, IBAN, types. Les horaires sont facultatifs. */}
          <Route path="/mise-en-route" component={MandatoryOnboardingPage} />
          {/* Assistant de première connexion : toujours passable. */}
          <Route path="/bienvenue" component={WelcomeWizardPage} />
          {/* Retours du prestataire de paiement. Rien ne s'y décide : c'est le rappel signé
              qui fait foi, et il peut arriver après le navigateur. */}
          <Route path="/abonnement/succes" component={SubscriptionSuccessPage} />
          <Route path="/abonnement/annule" component={SubscriptionCancelledPage} />
          {/* Console de plateforme. Chaque écran est gardé par le rôle : celui-ci vient de la
                configuration du serveur, et se recalcule à la connexion. */}
          <Route path="/plateforme">
            <Redirect to="/plateforme/apercu" replace />
          </Route>
          <Route path="/plateforme/apercu">
            <PlatformRoute>
              <PlatformOverviewPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/cabinets">
            <PlatformRoute>
              <PlatformOrganizationsPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/cabinets/:organizationId">
            <PlatformRoute>
              <PlatformOrganizationPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/utilisateurs">
            <PlatformRoute>
              <PlatformUsersPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/tickets">
            <PlatformRoute>
              <PlatformTicketsPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/tickets/:ticketId">
            <PlatformRoute>
              <PlatformTicketPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/services/:service?">
            <PlatformRoute>
              <PlatformServicesPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/journal">
            <PlatformRoute>
              <PlatformActivityPage />
            </PlatformRoute>
          </Route>
          <Route path="/plateforme/taches">
            <PlatformRoute>
              <PlatformActivityPage />
            </PlatformRoute>
          </Route>
          <Route component={NotFoundPage} />
        </Switch>
      </Suspense>
    </AppLayout>
  );
}

/**
 * Racine : le tableau de bord pour qui est connecté, la vitrine sinon. La V1 tranchait sur le
 * domaine (`osteopro.app` contre `osteo-pro.app`) avec un `window.location.replace` brutal ; ici la
 * page publique est simplement la racine de qui n'a pas de session.
 */
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <PageFallback />;
  if (isAuthenticated) return <Redirect to="/dashboard" replace />;
  return <LandingPage />;
}

export function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Switch>
        <Route path="/" component={RootRedirect} />
        <Route path="/login" component={LoginPage} />
        <Route path="/register" component={RegisterPage} />
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/accept-invitation" component={AcceptInvitationPage} />
        {/* Lien de vérification : public, il s'ouvre depuis une boîte mail, souvent ailleurs. */}
        <Route path="/verify-email" component={VerifyEmailPage} />
        <Route path="/booking/:token" component={BookingPage} />
        {/* Auto-inscription patient : publique, jeton d'organisation rotatif. */}
        <Route path="/p/:token" component={PublicRegistrationPage} />
        {/* Suivi post-consultation et avis : publics, jeton à usage unique, langue du dossier. */}
        <Route path="/follow-up/:token" component={FollowUpPage} />
        {/* Photo depuis le téléphone : publique, le jeton signé tient lieu de session. */}
        <Route path="/capture/:token" component={CapturePage} />
        <Route path="/review/:token" component={ReviewPage} />
        {/* Aperçu PDF peint dans la page : le visionneur natif laisse une page vide. */}
        <Route path="/apercu-pdf" component={PdfPreviewPage} />
        {/* Textes légaux : publics. En V1 ils exigeaient une session sur le domaine applicatif. */}
        <Route path="/mentions-legales">{() => <LegalPage slug="mentions-legales" />}</Route>
        <Route path="/confidentialite">{() => <LegalPage slug="confidentialite" />}</Route>
        <Route path="/cgu">{() => <LegalPage slug="cgu" />}</Route>
        <Route path="/rgpd">{() => <LegalPage slug="rgpd" />}</Route>
        <Route component={ProtectedArea} />
      </Switch>
    </Suspense>
  );
}
